/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Organization Balance Contract — endpoints for the per-org credit ledger.
 *
 * Two audience tiers share one contract:
 *   - Org admins: see their own credit balance and a transaction log
 *     (USD figures redacted by the service for non-superusers).
 *   - Superusers: per-org P&L (`usdMicrosTotal` / `usdMicrosThisMonth`)
 *     plus a manual credit endpoint for top-ups before Stripe integration.
 *
 * The hard-gate (`PRECONDITION_FAILED` / 412) is surfaced by AI endpoints
 * — not here. This contract exposes only the ledger CRUD surface; the
 * gating decision lives at the AI call site inside the assistant module.
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";

// ---------------------------------------------------------------------------
// Common shapes
// ---------------------------------------------------------------------------

export const BalanceTransactionKindSchema = z.enum([
	"CREDIT_MANUAL",
	"CREDIT_PAYMENT",
	"CREDIT_ADJUSTMENT",
	"DEBIT_AI_USAGE",
	"DEBIT_ADJUSTMENT",
	"REFUND",
]);
export type BalanceTransactionKind = z.infer<
	typeof BalanceTransactionKindSchema
>;

/**
 * One row in the per-org append-only ledger.
 *
 * `usdMicrosDelta` is null for non-superuser viewers — the service redacts
 * it before serialising. Clients should display "—" or hide the column.
 */
const BalanceTransactionItemSchema = z.object({
	id: z.string(),
	orgId: z.string(),
	kind: BalanceTransactionKindSchema,
	creditDelta: z.number().int(),
	/** Null for non-superuser callers. Decimal string over the wire. */
	usdMicrosDelta: z.string().nullable(),
	creditsAfter: z.number().int(),
	description: z.string().nullable(),
	/** AI model id from `metadata.modelId`; null for non-AI transactions. */
	modelId: z.string().nullable(),
	createdByUserId: z.string().nullable(),
	createdAt: z.string().datetime(),
});
export type BalanceTransactionItem = z.infer<
	typeof BalanceTransactionItemSchema
>;

// ---------------------------------------------------------------------------
// Inputs / Outputs
// ---------------------------------------------------------------------------

const GetBalanceOutputSchema = z.object({
	credits: z.number().int(),
});

const ListTransactionsInputSchema = z.object({
	cursor: z.string().nullable().optional(),
	/** Inclusive ISO `createdAt` lower bound. */
	dateFrom: z.string().datetime().optional(),
	/** Inclusive ISO `createdAt` upper bound. */
	dateTo: z.string().datetime().optional(),
	/** Comma-separated transaction kinds. */
	kinds: z.string().optional(),
	limit: z.number().int().min(1).max(100).default(20),
	/** Comma-separated AI model ids (matches `metadata.modelId`). */
	models: z.string().optional(),
	/** Row offset for page-based paging; ignored when `cursor` is set. */
	offset: z.number().int().min(0).optional(),
	/** Case-insensitive substring match on `description`. */
	search: z.string().max(200).optional(),
});
const ListTransactionsOutputSchema = z.object({
	items: z.array(BalanceTransactionItemSchema),
	nextCursor: z.string().nullable(),
	total: z.number().int(),
});

const TransactionFacetsOutputSchema = z.object({
	kinds: z.array(
		z.object({
			count: z.number().int(),
			kind: BalanceTransactionKindSchema,
		})
	),
	models: z.array(
		z.object({
			count: z.number().int(),
			modelId: z.string(),
		})
	),
});

const GetUsdLedgerInputSchema = z.object({
	orgId: z.string(),
});
const GetUsdLedgerOutputSchema = z.object({
	credits: z.number().int(),
	usdMicrosTotal: z.string(),
	usdMicrosThisMonth: z.string(),
});

const AdminCreditInputSchema = z.object({
	creditDelta: z.number().int().positive(),
	description: z.string().max(500).optional(),
});
const AdminCreditOutputSchema = z.object({
	creditsAfter: z.number().int(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const organizationBalanceContract = {
	get: oc
		.route({
			method: "GET",
			path: "/api/orgs/{orgId}/org-balance",
			summary: "Read the org's credit balance",
			tags: ["OrganizationBalance"],
		})
		.input(GetUsdLedgerInputSchema)
		.output(GetBalanceOutputSchema)
		.errors({ ...commonErrors }),

	listTransactions: oc
		.route({
			method: "GET",
			path: "/api/orgs/{orgId}/org-balance/transactions",
			summary:
				"Paged ledger history (usdMicrosDelta nulled for non-superuser callers)",
			tags: ["OrganizationBalance"],
		})
		.input(GetUsdLedgerInputSchema.merge(ListTransactionsInputSchema))
		.output(ListTransactionsOutputSchema)
		.errors({ ...commonErrors }),

	listTransactionFacets: oc
		.route({
			method: "GET",
			path: "/api/orgs/{orgId}/org-balance/transactions/facets",
			summary:
				"Distinct kinds + AI model ids present in the org's ledger, with counts",
			tags: ["OrganizationBalance"],
		})
		.input(GetUsdLedgerInputSchema)
		.output(TransactionFacetsOutputSchema)
		.errors({ ...commonErrors }),

	getUsdLedger: oc
		.route({
			method: "GET",
			path: "/api/orgs/{orgId}/org-balance/usd-ledger",
			summary: "Superuser-only USD-micros P&L for a specific org",
			tags: ["OrganizationBalance"],
		})
		.input(GetUsdLedgerInputSchema)
		.output(GetUsdLedgerOutputSchema)
		.errors({ ...commonErrors }),

	adminCredit: oc
		.route({
			method: "POST",
			path: "/api/orgs/{orgId}/org-balance/admin-credit",
			summary: "Superuser-only manual credit top-up for an org",
			tags: ["OrganizationBalance"],
		})
		.input(GetUsdLedgerInputSchema.merge(AdminCreditInputSchema))
		.output(AdminCreditOutputSchema)
		.errors({ ...commonErrors }),
};

export type OrganizationBalanceContract = typeof organizationBalanceContract;

export type GetBalanceOutput = z.infer<typeof GetBalanceOutputSchema>;
export type ListTransactionsInput = z.infer<typeof ListTransactionsInputSchema>;
export type ListTransactionsOutput = z.infer<
	typeof ListTransactionsOutputSchema
>;
export type TransactionFacetsOutput = z.infer<
	typeof TransactionFacetsOutputSchema
>;
export type GetUsdLedgerInput = z.infer<typeof GetUsdLedgerInputSchema>;
export type GetUsdLedgerOutput = z.infer<typeof GetUsdLedgerOutputSchema>;
export type AdminCreditInput = z.infer<typeof AdminCreditInputSchema>;
export type AdminCreditOutput = z.infer<typeof AdminCreditOutputSchema>;
